import type { Router } from "vue-router";
import i18n from "@/config/translation";
import head from "@/config/head";
import { isCurrentJobActive } from "@/services/resource";
import { useUserStore } from "@/config/store/userStore";
import { useApplicationStore } from "@/config/store/applicationStore";
import {
  canAccessManagementPage,
  getDefaultManagementHome,
} from "@/config/router/navigationsGuards/guardManagement";
import { trackPageView } from "@/services/userSession/trackPageView";
import { trackPendingNavigation } from "@/services/application/pendingNavigation";

/**
 * Take a router and add navigation guard for the app
 */
export default (router: Router) => {
  trackPendingNavigation(router);

  router.beforeEach((to) => {
    const userStore = useUserStore();
    const user = userStore.user;
    const routeName = to.name;
    const isOnLogin = routeName === "Login";
    const isOnCandidateOnboarding = routeName === "CandidateOnboarding";
    const isAuthRequired = to.meta.requiresAuth;
    const isManagementRequired = to.meta.requiresManagement;
    const isAdminUser = user?.role === "admin" && !user?._firmId;
    const isCurrentEmployee = isCurrentJobActive(user?.currentJob);
    if (routeName === "Disconnection") {
      userStore.disconnection();
      return { name: "Login" };
    }
    if (!isAuthRequired) return true;

    if (!isOnCandidateOnboarding && user && !isAdminUser && !isCurrentEmployee)
      return { name: "CandidateOnboarding" };

    // Admin users (role: 'admin' with no firmId) should go to management area
    const defaultHome = isAdminUser ? getDefaultManagementHome(user) : { name: "Home" };

    if (isOnLogin && user) return defaultHome;
    if (!user && !isOnLogin) {
      const applicationStore = useApplicationStore();

      applicationStore.previousApplicationRoute = to;

      return { name: "Login" };
    }

    if (!user && isOnLogin) return true;

    // Admin users don't have a profile - skip profile check for them
    if (!userStore.user.profile && !isAdminUser) {
      userStore.disconnection();
      return false;
    }

    // Admin users cannot access regular Home - redirect to management area
    if (isAdminUser && routeName === "Home") {
      return getDefaultManagementHome(user);
    }

    // Management belongs to the platform admin login: a firm account keeping the V1 'admin' role stays out
    if (isManagementRequired && !isAdminUser) return defaultHome;

    const managementPage = to.meta.managementPage;
    if (
      isManagementRequired &&
      isAdminUser &&
      typeof managementPage === "string" &&
      !canAccessManagementPage(managementPage, user)
    ) {
      return getDefaultManagementHome(user);
    }

    return true;
  });

  // The route title goes through unhead like the pages' useHead titles, so only one of them writes
  // document.title. Low priority: a page's own title wins, and the route title is back once it leaves.
  const routeTitle = head.push({ title: "BestConsultant" }, { tagPriority: "low" });

  router.afterEach((to, from, failure) => {
    const title = to.meta.titleTranslationKey;
    let documentTitle = "BestConsultant";
    if (typeof title === "string" && !failure) {
      documentTitle = `${i18n.global.t(title)} - BestConsultant`;
    }
    routeTitle.patch({ title: documentTitle });
    trackPageView(to, failure);
  });

  return router;
};
